/**
 * Lesson text generation.
 * ---------------------------------------------------------------------------
 * Exercises are generated, not stored. That keeps the bundle small, gives a
 * fresh exercise on every retry, and — critically — lets one rule be enforced
 * everywhere: a child is never shown a character they have not been taught.
 *
 * Generation is fully deterministic (see lib/rng.ts). The same (lessonId,
 * variant) pair always produces the same text, on the server and the client.
 *
 * A note on the space bar: it is used as a group separator from Lesson 1 even
 * though Level 5 is where it is formally taught. Unbroken 100-character runs
 * of "ffjjfj" are unreadable for a six-year-old, and every established tutor
 * chunks early drills the same way. Level 5 still owns space as its subject —
 * word separation, rhythm and one-thumb technique.
 */
import type { LessonDef, LevelDef } from '../types';
import { getLevel, LEVELS } from '../data/curriculum';
import {
  NUMBER_PHRASES,
  PARAGRAPHS,
  PLAIN_SENTENCES,
  SENTENCES,
  STORIES,
  SYMBOL_PHRASES,
  WORD_BANK,
} from '../data/content';
import { createRng, type Rng } from './rng';

/** Uppercase letters only become legal at Level 10, where Shift is taught. */
const SHIFT_LEVEL = 10;

function allowedSet(level: LevelDef): Set<string> {
  return new Set([...level.cumulativeKeys, ' ']);
}

/** Can `text` be typed using only what the child has been taught by `level`? */
function typable(text: string, level: LevelDef): boolean {
  const allowed = allowedSet(level);
  const shiftOk = level.index >= SHIFT_LEVEL;
  for (const ch of text) {
    if (ch === ' ' || ch === '\n') continue;
    const lower = ch.toLowerCase();
    if (lower !== ch && !shiftOk) return false;
    if (!allowed.has(lower)) return false;
  }
  return true;
}

/** Words spellable at this level, shortest first so early lessons stay gentle. */
function wordsFor(level: LevelDef): string[] {
  return WORD_BANK.filter((w) => typable(w, level)).sort((a, b) => a.length - b.length);
}

/* ─────────────────────────────────────────────────────────── key drills ─── */

/** The eight home-row resting keys plus the two inward index reaches. */
const HOME_ANCHORS = ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';'];

/**
 * Which keys a drill may interleave with its focus keys.
 *
 * NOT the full cumulative set. Once a child has met thirty keys, drawing
 * review characters from all of them produces unreadable noise — strings like
 * ",ce, ;,n cv" that teach nothing and look like a bug. Real tutors interleave
 * a new key against the *home row*, because that is the position the hand
 * must keep returning to.
 *
 * So: always the home row, plus this level's sibling keys when the level is
 * small enough that they are genuinely related (two-key levels drill against
 * each other; ten-key levels anchor on home instead).
 */
function reviewPool(level: LevelDef): string[] {
  const home = HOME_ANCHORS.filter((k) => level.cumulativeKeys.includes(k));
  const siblings = level.newKeys.length <= 6 ? level.newKeys : [];
  const pool = Array.from(new Set([...home, ...siblings]));
  // Levels 1–3 have not met the whole home row yet; fall back to what exists.
  return pool.length ? pool : level.cumulativeKeys;
}

/**
 * Build a run of drill groups. Focus keys dominate (they are the point of the
 * lesson) but review keys are always mixed in, because interleaved practice
 * retains far better than blocked repetition.
 */
function keyDrill(rng: Rng, focus: string[], review: string[], targetLength: number): string {
  const focusKeys = focus.filter((k) => k !== ' ');
  const reviewKeys = review.filter((k) => k !== ' ' && !focusKeys.includes(k));
  if (focusKeys.length === 0) return '';

  const draw = () =>
    reviewKeys.length && rng.next() < 0.3 ? rng.pick(reviewKeys) : rng.pick(focusKeys);

  const groups: string[] = [];
  let length = 0;

  // Open with pure repetition of each new key — the first contact with a key
  // should be boring and certain, not clever.
  for (const key of focusKeys) groups.push(key.repeat(3));

  // Then alternating pairs, which is what actually builds hand independence.
  if (focusKeys.length >= 2) {
    for (let i = 0; i < focusKeys.length - 1; i++) {
      const [a, b] = [focusKeys[i], focusKeys[i + 1]];
      groups.push(`${a}${b}${a}`, `${b}${a}${b}`);
    }
  }

  length = groups.join(' ').length;

  while (length < targetLength) {
    const size = 2 + rng.int(3); // 2–4 characters per chunk
    let group = '';
    for (let i = 0; i < size; i++) group += draw();
    groups.push(group);
    length += group.length + 1;
  }

  return groups.join(' ');
}

/* ──────────────────────────────────────────────────────────── word runs ─── */

function wordRun(rng: Rng, level: LevelDef, targetLength: number, maxWordLength = 99): string {
  const pool = wordsFor(level).filter((w) => w.length <= maxWordLength);
  if (pool.length === 0) return keyDrill(rng, level.cumulativeKeys, [], targetLength);

  const out: string[] = [];
  let length = 0;
  // sample() cycles the shuffled pool, so short banks repeat without clumping.
  for (const word of rng.sample(pool, 400)) {
    out.push(word);
    length += word.length + 1;
    if (length >= targetLength) break;
  }
  return out.join(' ');
}

/* ──────────────────────────────────────────────────────── prose selection ── */

/** The sentence pool appropriate to this level's taught key set. */
function sentencePool(level: LevelDef): string[] {
  const candidates =
    level.index >= SHIFT_LEVEL
      ? [...SENTENCES, ...NUMBER_PHRASES, ...SYMBOL_PHRASES]
      : level.index >= 9
        ? [...PLAIN_SENTENCES, ...NUMBER_PHRASES.map(stripPunctuation)]
        : PLAIN_SENTENCES;
  const usable = candidates.filter((s) => typable(s, level));
  return usable.length ? usable : PLAIN_SENTENCES.filter((s) => typable(s, level));
}

/** Downgrade a punctuated sentence for levels that have not met punctuation. */
function stripPunctuation(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function prose(rng: Rng, pool: string[], targetLength: number): string {
  if (pool.length === 0) return '';
  const out: string[] = [];
  let length = 0;
  for (const item of rng.sample(pool, 60)) {
    out.push(item);
    length += item.length + 1;
    if (length >= targetLength) break;
  }
  return out.join(' ');
}

/* ────────────────────────────────────────────────────────────── the API ─── */

export interface GeneratedLesson {
  /** The exact string the child will type. */
  text: string;
  /** Every distinct character in `text`, for the pre-lesson key preview. */
  chars: string[];
  /** Story metadata, when the lesson is a story. */
  story?: { title: string; emoji: string };
}

/**
 * Produce the text for one attempt at a lesson.
 *
 * @param variant bump this to get a different exercise for the same lesson
 *                (used for "try again" and for repeat visits).
 */
export function generateLesson(lesson: LessonDef, variant = 0): GeneratedLesson {
  const level = getLevel(lesson.levelId) ?? LEVELS[0];
  const rng = createRng(`${lesson.id}:${variant}`);

  // Timed lessons need enough text that a fast child cannot run out: allow for
  // 2× the target pace over the full duration, in characters (1 word = 5 chars).
  const targetLength = lesson.durationSec
    ? Math.max(lesson.length, Math.round((lesson.durationSec / 60) * lesson.targetWpm * 5 * 2))
    : lesson.length;

  let text = '';
  let story: GeneratedLesson['story'];

  switch (lesson.kind) {
    case 'keys':
      text = keyDrill(rng, lesson.focusKeys, reviewPool(level), targetLength);
      break;

    case 'words':
      // Cap word length early on so a Level 6 child is not handed "alfalfa".
      text = wordRun(rng, level, targetLength, level.index <= 6 ? 6 : 99);
      break;

    case 'sentences':
      text = prose(rng, sentencePool(level), targetLength);
      break;

    case 'paragraph':
      text = prose(
        rng,
        PARAGRAPHS.filter((p) => typable(p, level)),
        targetLength
      );
      break;

    case 'story': {
      const picked = STORIES[(lesson.index - 1) % STORIES.length];
      story = { title: picked.title, emoji: picked.emoji };
      text = picked.parts.join(' ');
      break;
    }

    case 'timed':
      text =
        level.index >= 11
          ? prose(rng, sentencePool(level), targetLength)
          : wordRun(rng, level, targetLength);
      break;

    case 'boss': {
      // The boss mixes every mode the level has practised, in order of
      // difficulty, so it genuinely tests the whole level rather than one part.
      const parts: string[] = [];
      if (lesson.focusKeys.length && level.index <= 5) {
        parts.push(
          keyDrill(rng, lesson.focusKeys, reviewPool(level), Math.round(targetLength * 0.5))
        );
      }
      const words = wordRun(rng, level, Math.round(targetLength * (level.index <= 5 ? 0.5 : 0.4)));
      if (words) parts.push(words);
      if (level.index >= 9) {
        parts.push(prose(rng, sentencePool(level), Math.round(targetLength * 0.6)));
      }
      text = parts.filter(Boolean).join(' ');
      break;
    }
  }

  text = normalize(text);
  if (!text) text = keyDrill(rng, level.cumulativeKeys.slice(0, 4), [], targetLength);

  return { text, chars: Array.from(new Set(text.split(''))).sort(), story };
}

/** Collapse whitespace and trim — the engine compares characters exactly. */
function normalize(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * A one-line preview of what a lesson contains, used on level pages and the
 * journey map so a child (or a parent) can see the shape before starting.
 */
export function lessonPreview(lesson: LessonDef): string {
  const { text } = generateLesson(lesson, 0);
  return text.length > 64 ? `${text.slice(0, 61)}…` : text;
}
